/**
 * DirectiveHistory - Direktif gecmisi
 *
 * Kullanicinin gonderdigi direktifleri gosterir.
 * Durum: pending, applied, rejected
 */

import { CheckCircle, Clock, XCircle } from 'lucide-react'
import { useEffect, useState } from 'react'

interface Directive {
  id: number
  text: string
  directive_type: string
  status: 'pending' | 'applied' | 'rejected'
  applied_to_cycle?: number
  created_at: string
}

interface DirectiveHistoryProps {
  projectId: number
}

export default function DirectiveHistory({ projectId }: DirectiveHistoryProps) {
  const [directives, setDirectives] = useState<Directive[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchDirectives = async () => {
      try {
        const response = await fetch(
          `${import.meta.env.VITE_API_URL || "http://localhost:8888"}/api/projects/${projectId}/directives`
        )
        if (response.ok) {
          const data = (await response.json()) as Directive[]
          setDirectives(data)
        }
      } catch (err) {
        console.error('Direktif gecmisi yuklenemedi:', err)
      } finally {
        setLoading(false)
      }
    }

    void fetchDirectives()
  }, [projectId])

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'applied':
        return <CheckCircle size={16} style={{ color: 'var(--success-color)' }} />
      case 'rejected':
        return <XCircle size={16} style={{ color: 'var(--danger-color)' }} />
      default:
        return <Clock size={16} style={{ color: 'var(--warning-color)' }} />
    }
  }

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      pending: 'Bekliyor',
      applied: 'Uygulandi',
      rejected: 'Reddedildi'
    }
    return labels[status] || status
  }

  const getStatusBadgeClass = (status: string) => {
    const classes: Record<string, string> = {
      pending: 'badge-warning',
      applied: 'badge-success',
      rejected: 'badge-danger'
    }
    return classes[status] || 'badge-secondary'
  }

  const getDirectiveTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      direction_change: 'Yon Degisikligi',
      question: 'Soru',
      add_resource: 'Kaynak',
      accelerate: 'Hizlandirma',
      stop: 'Durdurma',
      branch: 'Dal Acma'
    }
    return labels[type] || type
  }

  if (loading) {
    return (
      <div className="text-center py-4" style={{ color: 'var(--text-secondary)' }}>
        Yukleniyor...
      </div>
    )
  }

  if (directives.length === 0) {
    return (
      <div className="text-center py-4" style={{ color: 'var(--text-secondary)' }}>
        Henuz direktif yok
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {directives.map((directive) => (
        <div
          key={directive.id}
          className="p-3 rounded border"
          style={{
            background: 'var(--card-bg-secondary)',
            borderColor: 'var(--border-color)'
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              {getStatusIcon(directive.status)}
              <span className={`badge ${getStatusBadgeClass(directive.status)}`}>
                {getStatusLabel(directive.status)}
              </span>
            </div>
            <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
              {new Date(directive.created_at).toLocaleDateString('tr-TR')}
            </span>
          </div>

          {/* Direktif metni */}
          <p className="text-sm mb-2" style={{ color: 'var(--text-primary)' }}>
            {directive.text}
          </p>

          {/* Footer */}
          <div className="flex items-center justify-between text-xs">
            <span style={{ color: 'var(--text-tertiary)' }}>
              {getDirectiveTypeLabel(directive.directive_type)}
            </span>
            {directive.applied_to_cycle && (
              <span style={{ color: 'var(--text-tertiary)' }}>
                Cycle {directive.applied_to_cycle}
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}
